import { toVnDateString } from "@apv-erp/status";

/**
 * Tình trạng hạn của chứng từ cho dòng phụ trên danh sách (30/10/2026):
 * báo giá hết hiệu lực, đơn hàng / đơn mua / gia công / giao hàng đến hạn — tính theo NGÀY lịch Việt Nam.
 */
export type DeadlineTone = "done" | "cancelled" | "overdue" | "today" | "soon" | "ok" | "none";

export interface DeadlineStatus {
  label: string;
  tone: DeadlineTone;
  /** Số ngày còn lại (âm = quá hạn); null khi không có hạn / đã xong */
  days: number | null;
}

export interface DeadlineOptions {
  /** Chứng từ đã hoàn tất (đã chốt đơn, đã nhận đủ, đã giao…) */
  done?: boolean;
  doneLabel?: string;
  cancelled?: boolean;
  cancelledLabel?: string;
  /** Còn <= soonDays ngày thì nhắc (màu cam) */
  soonDays?: number;
  todayLabel?: string;
  overdueLabel?: (days: number) => string;
  /** Hôm nay theo giờ Việt Nam 'YYYY-MM-DD' (để test) */
  today?: string;
}

function dayNumber(ymd: string): number {
  const [y, m, d] = ymd.split("-").map(Number);
  return Math.round(Date.UTC(y, (m || 1) - 1, d || 1) / 86_400_000);
}

export function deadlineStatus(deadline: string | Date | null | undefined, opts: DeadlineOptions = {}): DeadlineStatus {
  if (opts.cancelled) return { label: opts.cancelledLabel || "Đã huỷ", tone: "cancelled", days: null };
  if (opts.done) return { label: opts.doneLabel || "Đã xong", tone: "done", days: null };
  if (!deadline) return { label: "Chưa có hạn", tone: "none", days: null };
  const due = toVnDateString(deadline as any);
  if (!due) return { label: "Chưa có hạn", tone: "none", days: null };
  const days = dayNumber(due) - dayNumber(opts.today || toVnDateString());
  if (days < 0) {
    return { label: opts.overdueLabel ? opts.overdueLabel(-days) : `Quá hạn ${-days} ngày`, tone: "overdue", days };
  }
  if (days === 0) return { label: opts.todayLabel || "Đến hạn hôm nay", tone: "today", days };
  return { label: `Còn ${days} ngày`, tone: days <= (opts.soonDays ?? 3) ? "soon" : "ok", days };
}

export const DEADLINE_TONE_CLASS: Record<DeadlineTone, string> = {
  done: "text-emerald-700",
  cancelled: "text-slate-400",
  overdue: "text-rose-600 font-semibold",
  today: "text-amber-700 font-semibold",
  soon: "text-amber-600",
  ok: "text-slate-500",
  none: "text-slate-400",
};
